import { TFunction } from "i18next";

/**
 * Maps backend error messages to translation keys
 * This allows backend error messages to be translated on the frontend
 */
const errorMessageMap: Record<string, string> = {
  "Store ID is required": "errors.storeIdRequired",
  "User not found": "errors.userNotFound",
  "Unauthorized": "errors.unauthorized",
  "Server error": "errors.serverError",
  "Invalid username/email or password. Please check your credentials and try again.": "errors.invalidCredentials",
  "Your account has been banned. Please contact support for assistance.": "errors.accountBanned",
  "Your password has expired. Please reset your password to continue.": "errors.passwordExpired",
  "Token is required": "errors.tokenRequired",
  "Invalid token": "errors.invalidToken",
  "Token has expired": "errors.tokenExpired",
  "Invalid token type": "errors.invalidTokenType",
  "Invalid token format": "errors.invalidTokenFormat",
  "Email already verified": "errors.emailAlreadyVerified",
  "Email does not exist or has already been verified": "errors.emailDoesNotExist",
  "User already exists": "errors.userAlreadyExists",
  "This password reset link has already been used or is invalid. Please request a new one.": "errors.passwordResetLinkUsed",
  "Current password is incorrect": "errors.currentPasswordIncorrect",
  "Store not found": "errors.storeNotFound",
  "Email is required": "errors.emailRequired",
  "Username/email and password are required": "errors.usernameOrPasswordRequired",
  "No token provided": "errors.noTokenProvided",
  "Authentication required": "errors.authenticationRequired",
  "Insufficient permissions": "errors.insufficientPermissions",
  "Forbidden": "errors.forbidden",
  "Cannot ban admin users": "admin.users.errors.cannotBanAdmin",
  "Cannot ban yourself": "admin.users.errors.cannotBanSelf",
  "Failed to fetch users": "admin.users.errors.fetch",
  "Failed to ban user": "admin.users.errors.ban",
  "Failed to unban user": "admin.users.errors.unban",
  "Failed to approve user": "admin.users.errors.failedToApprove",
  "Failed to delete user": "admin.users.errors.failedToDelete",
  "Failed to add user": "admin.users.errors.failedToAdd",
  "Failed to update profile": "admin.users.errors.failedToUpdateProfile",
  "Failed to fetch user orders": "admin.users.errors.failedToFetchOrders",
};

/**
 * Get translated error message from backend error response
 * @param error - Error object from API call
 * @param t - Translation function from i18next
 * @param fallbackKey - Optional fallback translation key if error message is not found
 * @returns Translated error message
 */
export const getTranslatedError = (
  error: any,
  t: TFunction,
  fallbackKey?: string
): string => {
  // Get error message from various possible locations
  const errorMessage =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    error?.error ||
    "";

  if (!errorMessage) {
    return fallbackKey ? t(fallbackKey) : t("errors.serverError");
  }

  // Check if we have a translation key for this error message
  const translationKey = errorMessageMap[errorMessage];

  if (translationKey) {
    const translated = t(translationKey);
    // If translation key doesn't exist, i18next returns the key itself
    // In that case, use the original error message
    return translated !== translationKey ? translated : errorMessage;
  }

  // If no translation key found, try to find partial matches
  // (for error messages that might have variable parts)
  const partialMatch = Object.keys(errorMessageMap).find((key) =>
    errorMessage.includes(key)
  );

  if (partialMatch) {
    const translationKey = errorMessageMap[partialMatch];
    const translated = t(translationKey);
    return translated !== translationKey ? translated : errorMessage;
  }

  // If no match found and we have a fallback, use it
  if (fallbackKey) {
    return t(fallbackKey);
  }

  // Last resort: return original error message
  return errorMessage;
};
